import { isDE, isUS } from '@helpers/countryHelpers';
import { t, isNil } from '@vivino/js-web-common';

import { getCurrentLocaleWithRegion, isChinese } from './locale';

const VC = 'VC';
const PPC = 'PPC';
const XDO = 'XDO';
const Linkout = 'linkout';

export const FREE = 'free';
export const INCLUDED = 'included';
export const DASH = 'dash';

const TRANSLATIONS = {
  free: 'components.shared.price.free',
  included: 'components.shared.price.included',
  pricePerLiter: 'components.shared.price.price_per_liter',
  save: 'components.shared.price.save',
  save_zh: 'components.shared.price.save_zh',
};

/*
 * XDO stands for "eXclusive Deals and Offers"
 * You can see more details here : https://vivino.slab.com/posts/abbreviations-559aeb1f
 */
function upperCasePriceType(price) {
  return price?.type?.toUpperCase();
}

export function isXDOPrice(price) {
  const type = upperCasePriceType(price);
  return type === XDO;
}

export function isVCPrice(price) {
  const type = upperCasePriceType(price);
  return type === VC;
}

export function isLinkoutPrice(price) {
  const type = price?.type;
  return type === Linkout;
}

export function isVivinoPrice(price) {
  return isVCPrice(price) || isXDOPrice(price) || isLinkoutPrice(price);
}

export function isPPCPrice(price) {
  const type = upperCasePriceType(price);
  return type === PPC;
}

export function isOfferPrice(price) {
  return Boolean(price?.deal);
}

export function discountPercent(price) {
  if (
    isNil(price) ||
    isNil(price.amount) ||
    isNil(price.discounted_from) ||
    price.discounted_from === 0
  ) {
    return 0;
  }

  return Math.round(((price.discounted_from - price.amount) * 100) / price.discounted_from);
}

/**
 * This higer order function creates a reducer based on the provided prices availability object.
 * It is meant to be used to reduce a list if vintage IDs to an object having vintageIDs as keys
 * and price objects as values. The resulting price values contains the amount, discounted_from, currency.
 *
 * @param {*} priceData price availability data as returned by https://api.testing.vivino.com/docs#/Prices/IndexAvailability
 * @param {*} withVintageSubstitution allow a vintages price to be substituted by another vintage price
 */
export const pricesKeyedByVintage =
  (priceData, withVintageSubstitution = false) =>
  (result, vintageId) => {
    const prices = priceData?.prices;
    /**
     * @TODO refactor this helper so that priceData param is of AvailabilityResult type, otherwise this helper can only be used with
     * the response from fetchPrices api since it's accessing a prices property. Once it's refactored, this helper can also be used by
     * SuperCardsBand (see comment in SuperCardsBand.tsx)
     * */
    const vintageAvailabilityResult = prices?.vintages?.[vintageId];
    const vintagePrice = vintageAvailabilityResult?.price;

    // price is not for this vintage
    const isSubstitutedPrice = vintageAvailabilityResult?.vintage?.id !== vintageId;

    if (!vintagePrice?.id) {
      return result;
    }

    if (!withVintageSubstitution && isSubstitutedPrice) {
      return result;
    }

    result[vintageId] = {
      ...vintagePrice,
      currency: prices?.market?.currency,
    };

    return result;
  };

export function addPercent({ amount, percent }) {
  return (percent * amount) / 100 + amount;
}
export function subtractPercent({ amount, percent }) {
  return amount - (percent * amount) / 100;
}

export const getRoundedPercentageDiscount = (price) => {
  if (isNil(price)) {
    return null;
  }
  const amount = price.amount;
  const discountFromAmount = price.discounted_from;

  if (!amount || !discountFromAmount || amount >= discountFromAmount) {
    return null;
  }

  return Math.round(((discountFromAmount - amount) * 100) / discountFromAmount);
};

/**
 * This returns a string that includes HTML string
 * which requires the use of dangerouslySetInnerHTML
 * Use vivino-ui/atoms/DiscountPercentLabel which is a wrapper
 * so that you can avoid direct use of dangerouslySetInnerHTML
 */
export const getDiscountPercentLabel = (price, options: any = {}) => {
  const discountPercent = getRoundedPercentageDiscount(price);

  if (!discountPercent) {
    return null;
  }

  if (isChinese()) {
    // "31% off" == "可享69折"
    // All Chinese-speaking regions prefer to think in "95折“ term
    // rather than "5% off" like other countries do
    // But with a special rule of not showing 0:
    // 10% off = 9折
    // 20% off = 8折
    // 30% off = 7折
    // 40% off = 6折
    // 50% off = 5折
    // 60% off = 4折
    // 70% off = 3折
    // 80% off = 2折
    // 90% off = 1折
    const percentOfOriginalPrice =
      (100 - discountPercent) % 10 === 0 ? (100 - discountPercent) / 10 : 100 - discountPercent;

    return t(TRANSLATIONS.save_zh, { percentOfOriginalPrice, percentClass: options.percentClass });
  }

  return t(TRANSLATIONS.save, { discountPercent, percentClass: options.percentClass });
};

export function formatNumber(amount) {
  if (!Number.isFinite(amount)) {
    return 0;
  }

  const fractionDigits = Number.isInteger(amount) ? 0 : 2;

  return Intl.NumberFormat(getCurrentLocaleWithRegion(), {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(amount);
}

export function priceToString({
  currency,
  amount,
  isNegative = false,
  showZeroAs = undefined,
  prependPlus = false,
}) {
  if (amount === null || amount === undefined) {
    return '';
  }

  if (amount === 0) {
    if (showZeroAs === FREE) {
      return t(TRANSLATIONS.free);
    } else if (showZeroAs === INCLUDED) {
      return t(TRANSLATIONS.included);
    } else if (showZeroAs === DASH) {
      return '-';
    }
    return (currency.prefix || '') + formatNumber(0) + (currency.suffix || '');
  }

  return (
    (isNegative ? '-' : '') +
    (!isNegative && prependPlus ? '+' : '') +
    (currency.prefix || '') +
    formatNumber(amount) +
    (currency.suffix || '')
  );
}

export const getZeroShippingLabelProp = (countryCode) => {
  return isUS(countryCode) ? INCLUDED : FREE;
};

export const isShowPricePerLiter = (countryCode) => isDE(countryCode);

export const pricePerLiterString = ({ currency, pricePerLiter }) =>
  t(TRANSLATIONS.pricePerLiter, {
    price_per_liter: priceToString({ currency, amount: pricePerLiter }),
  });

export const roundToNearest10 = (amount) => {
  if (isNil(amount)) {
    return 0;
  }

  return amount <= 10 ? Math.round(amount) : Math.round(amount / 10) * 10;
};

export const getPriceAmount = (calculatedPrice, price) =>
  calculatedPrice ? calculatedPrice.price_per_bottle : price.amount;

export const getDiscountedFromPrice = (calculatedPrice, price) =>
  calculatedPrice ? calculatedPrice.discount_from_price : price.discounted_from;

export const getPriceFromPricesAndAvilability = (pricesAndAvailability) => {
  const availablePrice = pricesAndAvailability?.availability?.price;
  const price = (pricesAndAvailability?.merchants_with_prices || []).reduce(
    (memo, merchantsWithPrice) => {
      merchantsWithPrice.prices.forEach((price) => {
        if (price.id === availablePrice?.id) {
          memo.push(price);
        }
      });
      return memo;
    },
    []
  )[0];
  if (!price) {
    return null;
  }
  return {
    ...price,
    type: availablePrice?.type,
  };
};
